/**
 * The star's popover: which lists these symbols are in, and a tick to change it.
 *
 * A component of its own rather than a `<ContextMenu>` with clever items,
 * because three of the things it does are not in that model and are the whole
 * point of this one: a **tri-state** tick, a **filter box** once the list of
 * lists outgrows a glance, and **staying open on a tick** so two lists cost one
 * visit. It copies that file's positioning and dismissal exactly, since those
 * are the parts that are easy to get wrong and are already right there.
 *
 * ## It closes on everything except a tick
 *
 * Escape, a click outside, a scroll of whatever it is anchored to, and choosing
 * `New list…`. Not on a tick — ticking is what you came to do, and you often do
 * two. Closing is never a decision the user can get wrong, because **every tick
 * is written the moment it is made**: there is no Save, no Cancel, and nothing
 * to lose by dismissing it.
 *
 * ## The half state adds, it does not remove
 *
 * With three rows selected and one already in a list, the tick reads `–` and
 * clicking it **adds the other two**. That is the only useful reading: a user
 * who wanted the one out would untick from `all`. `toggleSymbols` in
 * `lib/track-lists.ts` owns that rule so the cell, the bulk path and the key
 * handler cannot disagree about it.
 *
 * ## Colours come from tokens
 *
 * Every dot resolves through the injected resolver (D13). Nothing here holds a
 * colour, so a list looks like itself on Midnight and on White.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { tickState, type TrackList } from "../lib/track-lists";

/** Past this many lists the menu grows a filter box. */
const FILTER_THRESHOLD = 8;

const WIDTH = 218;

export interface TrackListMenuProps {
  /** Viewport rectangle of the star that opened it. */
  anchor: DOMRect;
  /** What the ticks act on — one row, or the whole selection. */
  symbols: readonly string[];
  lists: readonly TrackList[];
  /**
   * This list's colour, already resolved.
   *
   * Resolved by the host rather than here: the token resolver is injected at the
   * shell and a menu that reached for one of its own would be a second place a
   * theme is read.
   */
  colourOf: (list: TrackList) => string;
  /** A row was ticked. The menu stays open. */
  onToggle: (list: TrackList) => void;
  /** `New list…` — the menu closes and the dialog opens. */
  onCreate: () => void;
  /** Take these symbols out of every list. Only offered when some are in one. */
  onRemoveAll?: () => void;
  onClose: () => void;
}

export function TrackListMenu({
  anchor,
  symbols,
  lists,
  colourOf,
  onToggle,
  onCreate,
  onRemoveAll,
  onClose,
}: TrackListMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: anchor.right + 4, y: anchor.top });
  const [query, setQuery] = useState("");

  // Flip back inside the viewport once the real size is known — measured rather
  // than assumed, because the number of lists decides the height.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPos({
      x: Math.max(4, Math.min(anchor.right + 4, window.innerWidth - width - 4)),
      y: Math.max(4, Math.min(anchor.top, window.innerHeight - height - 4)),
    });
  }, [anchor.right, anchor.top, lists.length]);

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // Capture, and ignoring scrolls that start inside: the menu is anchored to a
    // grid cell, so a scroll of the grid moves the cell out from under it and
    // leaving it floating over the wrong row is worse than closing.
    const onScroll = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  const showFilter = lists.length > FILTER_THRESHOLD;
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return lists;
    return lists.filter((list) => list.name.toLowerCase().includes(needle));
  }, [lists, query]);

  const anyIn = lists.some((list) => tickState(list, symbols) !== "none");

  const heading =
    symbols.length === 1 ? symbols[0]! : `${symbols.length} symbols`;

  return (
    <div ref={ref} role="menu" style={{ ...MENU, left: pos.x, top: pos.y }}>
      <div style={HEAD}>
        {heading} &rarr;
      </div>

      {showFilter ? (
        <div style={{ padding: "2px 8px 6px" }}>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="type to filter…"
            style={INPUT}
          />
        </div>
      ) : null}

      <div style={SCROLL}>
        {visible.map((list) => {
          const state = tickState(list, symbols);
          return (
            <button
              key={list.id}
              role="menuitemcheckbox"
              aria-checked={state === "all"}
              style={ROW}
              onClick={() => onToggle(list)}
            >
              <span style={{ ...TICK, opacity: state === "none" ? 0 : 1 }}>
                {/* `–` for the half state, and it means "click to add the rest". */}
                {state === "all" ? "✓" : "–"}
              </span>
              <i style={{ ...DOT, background: colourOf(list) }} />
              <span style={NAME}>{list.name}</span>
              {list.hotKey ? <span style={KEYCAP}>{list.hotKey}</span> : null}
            </button>
          );
        })}
        {visible.length === 0 ? (
          <div style={EMPTY}>{lists.length ? "no list matches" : "no lists yet"}</div>
        ) : null}
      </div>

      <div style={SEP} />
      <button style={{ ...ROW, ...NEW }} onClick={onCreate}>
        + New list&hellip;
      </button>
      {anyIn && onRemoveAll ? (
        // The destructive one, behind a menu row rather than a keystroke: it is
        // the only action here that repeating does not undo.
        <button style={{ ...ROW, ...DANGER }} onClick={onRemoveAll}>
          Remove from all lists
        </button>
      ) : null}
    </div>
  );
}

// ── chrome ───────────────────────────────────────────────────────────────────
//
// `var(--qf-*)` throughout, never a hex: `ThemeProvider` publishes those on the
// shell root and this renders underneath it, so a theme switch repaints with no
// work here (see the app's CLAUDE.md).

const MENU: React.CSSProperties = {
  position: "fixed",
  zIndex: 1000,
  minWidth: WIDTH,
  maxWidth: 300,
  padding: "4px 0",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  boxShadow: "0 10px 30px -8px rgba(0,0,0,.55)",
  color: "var(--qf-text)",
  fontSize: 12,
};

const HEAD: React.CSSProperties = {
  padding: "5px 10px 6px",
  fontSize: 10,
  letterSpacing: ".05em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

// Capped so thirty lists scroll inside the menu rather than off the screen.
const SCROLL: React.CSSProperties = { maxHeight: "45vh", overflowY: "auto" };

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "5px 10px",
  border: 0,
  background: "transparent",
  color: "inherit",
  font: "inherit",
  fontSize: 11.5,
  textAlign: "left",
  cursor: "pointer",
};

const TICK: React.CSSProperties = {
  width: 11,
  flex: "none",
  color: "var(--qf-accent)",
  fontSize: 11,
};

const DOT: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: 1,
  flex: "none",
  display: "inline-block",
};

const NAME: React.CSSProperties = {
  flex: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const KEYCAP: React.CSSProperties = {
  minWidth: 15,
  textAlign: "center",
  fontFamily: "ui-monospace, Menlo, Consolas, monospace",
  fontSize: 10,
  color: "var(--qf-text-muted)",
  border: "1px solid var(--qf-border)",
  borderRadius: 2,
  padding: "0 3px",
  flex: "none",
};

const SEP: React.CSSProperties = {
  height: 1,
  background: "var(--qf-border)",
  margin: "4px 0",
};

const NEW: React.CSSProperties = { color: "var(--qf-accent)" };
const DANGER: React.CSSProperties = { color: "var(--qf-negative)" };

const EMPTY: React.CSSProperties = {
  padding: "8px 10px",
  color: "var(--qf-text-muted)",
  fontSize: 11,
};

const INPUT: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 3,
  padding: "3px 6px",
  color: "var(--qf-text)",
  font: "inherit",
  fontSize: 11.5,
};
